import type { ThreadMessage } from "@assistant-ui/core";

import type { ICitationResolution } from "@/api/chat/chat.types";
import {
    resolveCitationLinks,
    type CitationLinkContext,
} from "@/components/assistant-ui/utils/citation-links";

/** Serialize a thread's messages into a human-readable markdown string.
 * `options` carries the chat context used to turn opaque `cite://` markers
 * into openable deep-links (see {@link resolveCitationLinks}). */
export function threadToMarkdown(
    messages: readonly ThreadMessage[],
    options: CitationLinkContext = {}
): string {
    const blocks: string[] = [];
    for (const msg of messages) {
        if (msg.role !== "user" && msg.role !== "assistant") continue;
        const content = msg.content;
        if (!content) continue;
        // Only text parts are meaningful in the export. Tool-call, reasoning,
        // source, and data parts are runtime artifacts, not conversation content.
        const textParts = content.filter(
            (p): p is Extract<typeof p, { type: "text" }> => p.type === "text"
        );
        if (textParts.length === 0) continue;
        const resolutions = msg.metadata?.custom?.citation_resolutions as
            | ICitationResolution[]
            | undefined;
        const heading = msg.role === "user" ? "## User" : "## Assistant";
        blocks.push(
            `${heading}\n\n${textParts
                .map((p) => resolveCitationLinks(p.text, resolutions, options))
                .join("\n")}\n\n`
        );
    }
    return blocks.join("");
}
